import { Box, CircularProgress, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { UserDocument } from "../../models/UserDocument";
import { MouseEvent, useState } from "react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { atticusColors } from "../../theme";
import { DocumentThumbnail } from "./DocumentThumbnail";
import { displayFormattedDate } from "../../utils/displayDate";

interface Props {
  isLoading?: boolean,
  document: UserDocument;
  documentSrc?: string;
  onClick?: () => void;
  onDelete?: () => void,
  onDownload?: () => void,
}

export const DocumentItem = ({
  isLoading,
  document,
  documentSrc,
  onClick,
  onDelete,
  onDownload,
}: Props): JSX.Element => {
  const [contextMenuAnchor, setContextMenuAnchor] = useState<null | HTMLElement>(null);
  const contextMenuOpen = Boolean(contextMenuAnchor);

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenuAnchor(event.currentTarget);
  };
  const handleMenuClick = (evt: MouseEvent<HTMLElement>) => {
    evt.preventDefault()
    evt.stopPropagation()

    const id = evt.currentTarget?.dataset?.id;
    if (id === "view") {
      onClick?.();
    } else if (id === "delete") {
      console.log(onDelete || "on onDelete")
      onDelete?.();
    } else if (id === "download") {
      onDownload?.()
    }
    setContextMenuAnchor(null);
  };

  return (
    <Box
      title={
        document?.notes || document?.version?.title || "click to view file"
      }
      component="button"
      aria-label={document?.version?.title}
      onClick={onClick}
      sx={{
        cursor: "pointer",
        width: "100%",
        border: "none",
        padding: 0,
        backgroundColor: "transparent",
        textAlign: "left",
        "&:focus-visible, &:focus": {
          outline: "none",
        },
        height: "275px",
      }}
    >
      <Box
        sx={{
          padding: 0,
          marginTop: "-1rem",
          backgroundColor: atticusColors.fileBackgroundBlue,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderBottom: "solid 1px",
          borderColor: "grey.400",
          width: "100%",
          height: "180px",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {isLoading && <div style={{ position: "absolute", top: "1rem", left: "1rem" }}>
          <CircularProgress style={{ opacity: 0.2 }} />
        </div>}
        <DocumentThumbnail document={document} documentSrc={documentSrc} />
      </Box>
      <Box
        sx={{
          width: "100%",
          padding: "0 0 0 1rem",
          display: "flex",
          justifyContent: "space-between",
          height: "5rem",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }} >
          <Typography
            component={"div"}
            sx={{
              fontWeight: "bold",
              maxWidth: "130px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              paddingTop: "0.5rem",
              height: "3.5rem",
            }}
          >
            {document?.version?.title}
          </Typography>
          <Typography component={"div"} variant="body2" color="#999" style={{ overflow: "hidden", whiteSpace: "nowrap" }}>
            {displayFormattedDate(document?.version?.updatedAt || document?.version?.createdAt)}
          </Typography>
        </div>
        <div>
          <IconButton
            style={{ width: "40px", height: "40px" }}
            id="more-button"
            aria-controls={contextMenuOpen ? "asset-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={contextMenuOpen ? "true" : undefined}
            onClick={handleMenuOpen}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            id="asset-menu"
            anchorEl={contextMenuAnchor}
            open={contextMenuOpen}
            onClose={handleMenuClick}
            MenuListProps={{
              "aria-labelledby": "more-button",
            }}
          >
            {document.version?.cloudUrl && <MenuItem key="viewinclouditem" data-id="view-in-cloud">
              <Typography component="a" variant="body1" color={"MenuText"} href={document.version?.cloudUrl} target="_blank"
                sx={{
                  '&:hover': {
                    color: 'black',
                    textDecoration: "none",
                  }
                }}>
                View in cloud
              </Typography>
            </MenuItem>}
            {!document.version?.cloudUrl && <MenuItem key="downloaditem" onClick={handleMenuClick} data-id="download">
              <Typography component="div" variant="body1" color={"MenuText"}>
                Download file
              </Typography>
            </MenuItem>}
            <MenuItem key="viewitem" onClick={handleMenuClick} data-id="view">
              <Typography component="div" variant="body1" color={"MenuText"}>
                View file
              </Typography>
            </MenuItem>
            <MenuItem key="deleteitem" onClick={handleMenuClick} data-id="delete">
              <Typography component="div" variant="body1" color={"MenuText"}>
                Delete file
              </Typography>
            </MenuItem>
          </Menu>
        </div>
      </Box>
    </Box >
  );
};